import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ActionService } from '../services/actionService';
import { ProjectPlanService } from '../services/projectPlanService';
import { ProjectRoadmapService } from '../services/projectRoadmapService';
import { ProjectService } from '../services/projectService';
import {
  buildProjectDashboardData,
  emptyProjectDashboardSummary,
  logProjectDashboardCounts,
  type ProjectDashboardData,
} from '../services/projectDashboardData';
import { useProjectStore } from '../store/useProjectStore';
import { hydrateProjectIntoStore, normalizeSupabaseProjectRow, type SupabaseProjectRow } from '../utils/projectHydration';

export function useProjectDashboardData(
  orgId: string | undefined,
  projectId: string | undefined,
): {
  data: ProjectDashboardData | null;
  isLoading: boolean;
  summary: ReturnType<typeof emptyProjectDashboardSummary>;
  actions: ProjectDashboardData['actions'];
  vsmCards: ProjectDashboardData['vsmCards'];
  hasVsm: boolean;
} {
  const localActions = useProjectStore((state) => state.actions);
  const hydratedFingerprintRef = useRef<string | null>(null);
  const loggedFingerprintRef = useRef<string | null>(null);

  const { data: projectRow, isLoading: projectLoading } = useQuery({
    queryKey: ['project', orgId, projectId],
    queryFn: () => ProjectService.getById(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: enterpriseActions = [], isLoading: actionsLoading } = useQuery({
    queryKey: ['project-dashboard-actions', orgId, projectId],
    queryFn: () => ActionService.list(orgId!, { project_id: projectId! }),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: roadmapItems = [], isLoading: roadmapLoading } = useQuery({
    queryKey: ['roadmap', orgId, projectId],
    queryFn: () => ProjectRoadmapService.list(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: issues = [], isLoading: issuesLoading } = useQuery({
    queryKey: ['project-issues', orgId, projectId],
    queryFn: () => ProjectService.listProjectIssues(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: executions = [], isLoading: executionsLoading } = useQuery({
    queryKey: ['project-executions', orgId, projectId],
    queryFn: () => ProjectService.listProjectExecutions(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: blueprints = [], isLoading: blueprintsLoading } = useQuery({
    queryKey: ['project-blueprints', orgId, projectId],
    queryFn: () => ProjectService.listProjectBlueprints(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['project-plans', orgId, projectId],
    queryFn: () => ProjectPlanService.listPlans(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 30_000,
  });

  const { data: planMetrics = [], isLoading: metricsLoading } = useQuery({
    queryKey: ['project-plan-metrics', orgId, projectId],
    queryFn: () => ProjectPlanService.listPlanMetrics(orgId!, projectId!),
    enabled: !!orgId && !!projectId,
    staleTime: 60_000,
  });

  const projectLocalActions = useMemo(() => {
    const filtered = localActions.filter((action) => action.projectId === projectId);
    if (enterpriseActions.length === 0) return filtered;
    const enterpriseIds = new Set(enterpriseActions.map((action) => action.id));
    return filtered.filter((action) => enterpriseIds.has(action.id));
  }, [localActions, projectId, enterpriseActions]);

  const dashboard = useMemo(() => {
    if (!projectRow || !projectId) return null;
    return buildProjectDashboardData({
      projectRow,
      enterpriseActions,
      localActions: projectLocalActions,
      roadmapItems,
      issues,
      executions,
      blueprints,
      plans,
      planMetrics,
    });
  }, [
    projectRow,
    projectId,
    enterpriseActions,
    projectLocalActions,
    roadmapItems,
    issues,
    executions,
    blueprints,
    plans,
    planMetrics,
  ]);

  // Hidrata o store uma única vez por projeto/versão — evita loop com setState.
  useEffect(() => {
    if (!projectRow || !projectId) return;

    const fingerprint = `${projectId}:${projectRow.updated_at ?? ''}`;
    if (hydratedFingerprintRef.current === fingerprint) return;

    const mapProject = normalizeSupabaseProjectRow(projectRow as SupabaseProjectRow);
    if (mapProject.id !== projectId) return;

    const { setPostIts, setOptimizationLayer } = useProjectStore.getState();
    hydrateProjectIntoStore(mapProject, { setPostIts, setOptimizationLayer });
    hydratedFingerprintRef.current = fingerprint;
  }, [projectRow, projectId]);

  useEffect(() => {
    if (!dashboard) return;

    const fingerprint = [
      dashboard.project.id,
      dashboard.project.updated_at,
      dashboard.counts.processos,
      dashboard.counts.elementosVsm,
      dashboard.counts.acoes,
      dashboard.counts.problemas,
      dashboard.counts.marcos,
      dashboard.summary.progress,
    ].join(':');

    if (loggedFingerprintRef.current === fingerprint) return;
    logProjectDashboardCounts(dashboard);
    loggedFingerprintRef.current = fingerprint;
  }, [dashboard]);

  useEffect(() => {
    hydratedFingerprintRef.current = null;
    loggedFingerprintRef.current = null;
  }, [projectId]);

  const isLoading =
    projectLoading
    || actionsLoading
    || roadmapLoading
    || issuesLoading
    || executionsLoading
    || blueprintsLoading
    || plansLoading
    || metricsLoading;

  return {
    data: dashboard,
    isLoading,
    summary: dashboard?.summary ?? emptyProjectDashboardSummary(),
    actions: dashboard?.actions ?? [],
    vsmCards: dashboard?.vsmCards ?? [],
    hasVsm: dashboard?.hasVsm ?? false,
  };
}
